import { date, num, pct, tonnes } from '@/lib/format';
import type { DashboardSummary } from '@/types/models';

type Tone = 'accent' | 'green' | 'amber' | 'red';

const FILL: Record<Tone, string> = {
  accent: 'var(--brand-teal)',
  green: 'var(--fill-green)',
  amber: 'var(--fill-amber)',
  red: 'var(--fill-red)',
};

function tone(p: number | null | undefined): Tone {
  if (p == null) return 'accent';
  return p >= 95 ? 'green' : p >= 80 ? 'amber' : 'red';
}

function Meter({ value, fill, label }: { value: number | null | undefined; fill: string; label: string }) {
  const w = value == null || !Number.isFinite(value) ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div className="meter" role="img" aria-label={label}>
      <span style={{ width: `${w}%`, background: fill }} />
    </div>
  );
}

/**
 * The four figures a manager reads first (Suremix brand, docs/26): today's
 * tonnes, the month against its target, the fleet, and the blast's cover.
 * Each has a bar, so "how far along" reads without the number.
 */
export function Headline({ data, isToday }: { data: DashboardSummary; isToday: boolean }) {
  const { today, fleet, production: p, blast } = data;
  const mtdTone: Tone = p.target_basis === 'pro_rata' ? tone(p.achievement_mtd_pct) : 'accent';
  const blastLeft = blast.original_tonnes && blast.remaining_tonnes != null
    ? (Number(blast.remaining_tonnes) / Number(blast.original_tonnes)) * 100 : null;
  const blastTone: Tone = blast.status_colour === 'red' ? 'red' : blast.status_colour === 'amber' ? 'amber' : 'accent';
  const up = fleet.machines_available ?? 0;
  const down = fleet.machines_down ?? 0;

  return (
    <section className="headline" aria-label="Headline figures">
      <div className="headline__card">
        <div className="stat__label">{isToday ? 'Production today' : `Production ${date(data.as_at)}`}</div>
        <div className="headline__value">
          {tonnes(today.tonnes_production)}
          <span className="headline__unit"> / {tonnes(today.target_tonnes)} t</span>
        </div>
        <Meter value={today.achievement_pct} fill={FILL[isToday ? 'accent' : tone(today.achievement_pct)]}
          label={`${pct(today.achievement_pct)} % of the day's target`} />
        <div className="headline__sub">
          {isToday ? 'Shift in progress · ' : ''}{num(today.operating_hours, 1)} operating h
        </div>
      </div>

      <div className="headline__card">
        <div className="stat__label">Month to date</div>
        <div className="headline__value">{tonnes(p.tonnes_mtd)}<span className="headline__unit"> t</span></div>
        <Meter value={p.achievement_mtd_pct} fill={FILL[mtdTone]}
          label={`${pct(p.achievement_mtd_pct)} % of the target to date`} />
        <div className={`headline__sub${mtdTone === 'amber' || mtdTone === 'red' ? ` headline__sub--${mtdTone}` : ''}`}>
          {p.target_mtd
            ? `${pct(p.achievement_mtd_pct)} % of ${tonnes(p.target_mtd)} t ${p.target_basis === 'full_month' ? 'this month' : 'to date'}`
            : 'No target set for the month'}
        </div>
      </div>

      <div className="headline__card">
        <div className="stat__label">Fleet availability</div>
        <div className={`headline__value headline__value--${tone(fleet.availability_mtd) === 'green' ? 'green' : 'plain'}`}>
          {pct(fleet.availability_mtd)}<span className="headline__unit"> % MTD</span>
        </div>
        <div className="meter meter--split" role="img" aria-label={`${up} machines working, ${down} down`}>
          <span style={{ flexGrow: up || 0.0001, background: FILL.green }} />
          {down > 0 && <span style={{ flexGrow: down, background: FILL.red }} />}
        </div>
        <div className="headline__sub">
          {up} of {fleet.machines_total} machines working · {utilLine(fleet)}
        </div>
      </div>

      <div className="headline__card">
        <div className="stat__label">Blast stock</div>
        <div className="headline__value">
          {num(blast.production_days_remaining, 1)}<span className="headline__unit"> days</span>
        </div>
        <Meter value={blastLeft} fill={FILL[blastTone]} label={`${num(blastLeft, 0)} % of the blast left`} />
        <div className={`headline__sub${blastTone !== 'accent' ? ` headline__sub--${blastTone}` : ''}`}>
          {tonnes(blast.remaining_tonnes)} t left
          {blast.expected_depletion_date ? ` · runs out ${date(blast.expected_depletion_date)}` : ''}
        </div>
      </div>
    </section>
  );
}

/** "plant utilisation 88,9 % · vehicles 93,0 %", month to date (0044). A
 *  summary cached before 0044 has only the single diary figure. */
function utilLine(f: DashboardSummary['fleet']): string {
  if (f.plant_utilisation_mtd === undefined) return `utilisation ${pct(f.utilisation_mtd)} %`;
  const parts: string[] = [];
  if ((f.plant_count ?? 0) > 0 || (f.vehicle_count ?? 0) === 0) parts.push(`plant utilisation ${pct(f.plant_utilisation_mtd)} %`);
  if ((f.vehicle_count ?? 0) > 0) parts.push(`vehicles ${pct(f.vehicle_utilisation_mtd)} %`);
  return parts.join(' · ');
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December'];

/** "Good morning — Thursday 25 September", and when the figures are from. */
export function Greeting({ data, isToday }: { data: DashboardSummary; isToday: boolean }) {
  // Names spelt out here, not by toLocale*: one output on every device.
  const d = new Date(`${data.as_at}T12:00:00`);
  const day = Number.isNaN(d.getTime()) ? date(data.as_at)
    : `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  const h = new Date().getHours();
  const hello = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const at = new Date(data.generated_at);
  const clock = Number.isNaN(at.getTime()) ? ''
    : `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
  return (
    <div className="greeting">
      <h1 className="greeting__title">{isToday ? `${hello} — ${day}` : day}</h1>
      <div className="greeting__sub">
        Suremix Quarry{clock ? ` · figures to ${isToday ? '' : `${date(data.generated_at)} `}${clock}` : ''}
      </div>
    </div>
  );
}
